import React, { useState, useRef } from 'react';
import { Card, Row, Col, Statistic, Select, Space, Table, Divider, Tag, Typography, Button, Descriptions } from 'antd';
import { DollarOutlined, UserOutlined, BookOutlined, RiseOutlined, FallOutlined, PrinterOutlined, TeamOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { useReactToPrint } from 'react-to-print';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const { Title, Text } = Typography;
const { Option } = Select;

const CourseReport = () => {
    const { t } = useTranslation();
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const reportRef = useRef();

    const handlePrint = useReactToPrint({
        content: () => reportRef.current,
    });

    // Fetch all data
    const { data: coursesData, isLoading: coursesLoading } = useQuery('courses', async () => {
        const response = await api.get('/courses');
        return response.data.data;
    });

    const { data: studentsData, isLoading: studentsLoading } = useQuery('all-students-report', async () => {
        const response = await api.get('/students', { params: { limit: 10000 } });
        return response.data.data;
    });

    const { data: staffData, isLoading: staffLoading } = useQuery('all-staff-report', async () => {
        const response = await api.get('/staff', { params: { limit: 10000 } });
        return response.data.data;
    });

    const { data: expensesData, isLoading: expensesLoading } = useQuery('expenses', async () => {
        const response = await api.get('/expenses');
        response.data.data.forEach(exp => {
            console.log('Expense:', {
                _id: exp._id,
                description: exp.description,
                amount: exp.amount,
                currency: exp.currency,
                relatedTo: exp.relatedTo
            });
        });
        return response.data.data;
    });

    const formatCurrency = (amount, currency = 'USD') => {
        const formatted = (amount || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
        return formatted;
    };

    const getCurrencyPrefix = (currency = 'USD') => {
        return currency === 'IQD' ? 'IQD ' : '$ ';
    };

    // Get selected course
    const selectedCourse = coursesData?.find(c => c._id === selectedCourseId);

    // Get the course's currency
    const courseCurrency = selectedCourse?.fee?.currency || 'USD';

    // Calculate course statistics
    const calculateCourseStats = () => {
        if (!selectedCourse || !studentsData || !staffData || !expensesData) {
            return {
                studentCount: 0,
                totalIncomeUSD: 0,
                totalIncomeIQD: 0,
                totalCommissionUSD: 0,
                totalCommissionIQD: 0,
                totalExpensesUSD: 0,
                totalExpensesIQD: 0,
                netProfitUSD: 0,
                netProfitIQD: 0,
                students: [],
                staffCommissionsUSD: [],
                staffCommissionsIQD: [],
                expensesUSD: [],
                expensesIQD: []
            };
        }

        const courseId = selectedCourse._id;

        // Find all students enrolled in this course
        const enrolledStudents = [];
        let totalIncomeUSD = 0;
        let totalIncomeIQD = 0;

        studentsData.forEach(student => {
            student.enrollments?.forEach(enrollment => {
                const enrollmentCourseId = enrollment.course?._id || enrollment.course;
                if (String(enrollmentCourseId) === String(courseId)) {
                    const paidAmount = enrollment.payment?.paidAmount || 0;
                    const currency = enrollment.payment?.currency || 'USD';

                    if (currency === 'USD') {
                        totalIncomeUSD += paidAmount;
                    } else {
                        totalIncomeIQD += paidAmount;
                    }

                    enrolledStudents.push({
                        studentId: student._id,
                        studentName: `${student.firstName} ${student.lastName}`,
                        phone: student.phone,
                        totalFee: enrollment.payment?.totalFee || 0,
                        paidAmount: paidAmount,
                        remainingAmount: enrollment.payment?.remainingAmount || 0,
                        currency: currency,
                        status: enrollment.status
                    });
                }
            });
        });

        // Calculate staff commissions for this course (separate by currency)
        // First, calculate expenses that affect commission
        const courseExpensesForCommission = expensesData.filter(expense => {
            const relatedCourseId = expense.relatedTo?.course?._id ||
                expense.relatedTo?.course ||
                (expense.relatedTo?.model === 'Course' ? (expense.relatedTo?.id?._id || expense.relatedTo?.id) : null);
            const matches = String(relatedCourseId) === String(courseId);
            return matches && expense.affectsCommission === true;
        });

        const expensesAffectingCommissionUSD = courseExpensesForCommission
            .filter(exp => exp.currency === 'USD')
            .reduce((sum, exp) => sum + (exp.amount || 0), 0);

        const expensesAffectingCommissionIQD = courseExpensesForCommission
            .filter(exp => exp.currency === 'IQD')
            .reduce((sum, exp) => sum + (exp.amount || 0), 0);

        // Calculate base amount for commission (income - expenses that affect commission)
        const commissionBaseUSD = totalIncomeUSD - expensesAffectingCommissionUSD;
        const commissionBaseIQD = totalIncomeIQD - expensesAffectingCommissionIQD;

        const staffCommissionsUSD = [];
        const staffCommissionsIQD = [];
        let totalCommissionUSD = 0;
        let totalCommissionIQD = 0;

        staffData.forEach(staff => {
            if (!staff.commission?.enabled) return;

            // Check if commission applies to this course
            const appliesTo = staff.commission.appliesTo;
            const specificCourses = staff.commission.specificCourses || [];

            let applies = false;
            if (appliesTo === 'all' || appliesTo === 'all-income' || appliesTo === 'course-income') {
                applies = true;
            } else if ((appliesTo === 'specific' || appliesTo === 'specific-courses') && specificCourses.some(sc => String(sc) === String(courseId))) {
                applies = true;
            }

            if (applies) {
                let commissionAmountUSD = 0;
                let commissionAmountIQD = 0;

                if (staff.commission.type === 'percentage') {
                    commissionAmountUSD = (commissionBaseUSD * staff.commission.percentage) / 100;
                    commissionAmountIQD = (commissionBaseIQD * staff.commission.percentage) / 100;
                } else if (staff.commission.type === 'fixed') {
                    const fixedAmount = staff.commission.fixedAmount || 0;
                    // Apply fixed amount to the currency that has income
                    if (commissionBaseUSD > 0) commissionAmountUSD = fixedAmount;
                    if (commissionBaseIQD > 0) commissionAmountIQD = fixedAmount;
                }

                if (commissionAmountUSD > 0) {
                    totalCommissionUSD += commissionAmountUSD;
                    staffCommissionsUSD.push({
                        staffId: staff._id,
                        staffName: `${staff.firstName} ${staff.lastName}`,
                        position: staff.position,
                        commissionType: staff.commission.type,
                        commissionValue: staff.commission.type === 'percentage'
                            ? `${staff.commission.percentage}%`
                            : formatCurrency(staff.commission.fixedAmount, 'USD'),
                        commissionAmount: commissionAmountUSD
                    });
                }

                if (commissionAmountIQD > 0) {
                    totalCommissionIQD += commissionAmountIQD;
                    staffCommissionsIQD.push({
                        staffId: staff._id,
                        staffName: `${staff.firstName} ${staff.lastName}`,
                        position: staff.position,
                        commissionType: staff.commission.type,
                        commissionValue: staff.commission.type === 'percentage'
                            ? `${staff.commission.percentage}%`
                            : formatCurrency(staff.commission.fixedAmount, 'IQD'),
                        commissionAmount: commissionAmountIQD
                    });
                }
            }
        });

        // Calculate course expenses (separate by currency)
        console.log('\n=== FILTERING EXPENSES FOR COURSE ===');
        console.log('Course ID:', courseId);
        console.log('Total expenses to check:', expensesData.length);

        const courseExpenses = expensesData.filter(expense => {
            // Debug logging
            console.log('Checking expense:', expense._id, {
                description: expense.description,
                relatedTo: expense.relatedTo,
                courseId: courseId
            });

            // Check multiple ways the course might be referenced
            // The relatedTo.id can be either a string or a populated object
            const relatedCourseId = expense.relatedTo?.course?._id ||
                expense.relatedTo?.course ||
                (expense.relatedTo?.model === 'Course' ? (expense.relatedTo?.id?._id || expense.relatedTo?.id) : null);

            const matches = String(relatedCourseId) === String(courseId);

            console.log('Related Course ID:', relatedCourseId, 'Matches:', matches);

            if (matches) {
                console.log('✓ Expense matched for course:', expense._id);
            }

            return matches;
        });

        console.log('Total expenses matched:', courseExpenses.length);

        const expensesUSD = courseExpenses.filter(exp => exp.currency === 'USD');
        const expensesIQD = courseExpenses.filter(exp => exp.currency === 'IQD');

        const totalExpensesUSD = expensesUSD.reduce((sum, expense) => sum + (expense.amount || 0), 0);
        const totalExpensesIQD = expensesIQD.reduce((sum, expense) => sum + (expense.amount || 0), 0);

        // Calculate net profit (Total Income - Commission - Expenses)
        const netProfitUSD = totalIncomeUSD - totalCommissionUSD - totalExpensesUSD;
        const netProfitIQD = totalIncomeIQD - totalCommissionIQD - totalExpensesIQD;

        return {
            studentCount: enrolledStudents.length,
            totalIncomeUSD,
            totalIncomeIQD,
            totalCommissionUSD,
            totalCommissionIQD,
            totalExpensesUSD,
            totalExpensesIQD,
            netProfitUSD,
            netProfitIQD,
            students: enrolledStudents,
            staffCommissionsUSD,
            staffCommissionsIQD,
            expensesUSD: expensesUSD.map(exp => ({
                id: exp._id,
                description: exp.description,
                amount: exp.amount,
                currency: exp.currency,
                date: exp.date,
                expenseType: exp.expenseType?.name || 'N/A'
            })),
            expensesIQD: expensesIQD.map(exp => ({
                id: exp._id,
                description: exp.description,
                amount: exp.amount,
                currency: exp.currency,
                date: exp.date,
                expenseType: exp.expenseType?.name || 'N/A'
            }))
        };
    };

    const stats = calculateCourseStats();

    // Table columns
    const studentColumns = [
        {
            title: t('studentName'),
            dataIndex: 'studentName',
            key: 'studentName',
        },
        {
            title: t('phone'),
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: t('totalFee'),
            dataIndex: 'totalFee',
            key: 'totalFee',
            render: (val, record) => formatCurrency(val, record.currency),
        },
        {
            title: t('paidAmount'),
            dataIndex: 'paidAmount',
            key: 'paidAmount',
            render: (val, record) => formatCurrency(val, record.currency),
        },
        {
            title: t('remainingAmount'),
            dataIndex: 'remainingAmount',
            key: 'remainingAmount',
            render: (val, record) => formatCurrency(val, record.currency),
        },
        {
            title: t('status'),
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const colors = {
                    active: 'green',
                    completed: 'blue',
                    inactive: 'orange'
                };
                return <Tag color={colors[status] || 'default'}>{status?.toUpperCase()}</Tag>;
            }
        },
    ];

    const commissionColumns = (currency) => [
        {
            title: t('staffName'),
            dataIndex: 'staffName',
            key: 'staffName',
        },
        {
            title: t('position'),
            dataIndex: 'position',
            key: 'position',
        },
        {
            title: t('commissionType'),
            dataIndex: 'commissionType',
            key: 'commissionType',
            render: (type) => type === 'percentage' ? t('percentage') : t('fixedAmount'),
        },
        {
            title: t('commissionValue'),
            dataIndex: 'commissionValue',
            key: 'commissionValue',
        },
        {
            title: t('commissionAmount'),
            dataIndex: 'commissionAmount',
            key: 'commissionAmount',
            render: (val) => formatCurrency(val, currency),
        },
    ];

    const expenseColumns = [
        {
            title: t('description'),
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: t('expenseType'),
            dataIndex: 'expenseType',
            key: 'expenseType',
        },
        {
            title: t('amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (val, record) => formatCurrency(val, record.currency),
        },
        {
            title: t('date'),
            dataIndex: 'date',
            key: 'date',
            render: (date) => new Date(date).toLocaleDateString(),
        },
    ];

    const isLoading = coursesLoading || studentsLoading || staffLoading || expensesLoading;

    return (
        <div>
            <div className="page-header">
                <Title level={2}>{t('courseReport')}</Title>
                <Space>
                    <Select
                        placeholder={t('selectCourse')}
                        style={{ width: 300 }}
                        onChange={setSelectedCourseId}
                        value={selectedCourseId}
                        loading={coursesLoading}
                        showSearch
                        optionFilterProp="children"
                    >
                        {coursesData?.map(course => (
                            <Option key={course._id} value={course._id}>
                                {course.code} - {course.name}
                            </Option>
                        ))}
                    </Select>
                    {selectedCourseId && (
                        <Button icon={<PrinterOutlined />} onClick={handlePrint}>
                            {t('print')}
                        </Button>
                    )}
                </Space>
            </div>

            {selectedCourse && (
                <div ref={reportRef} style={{ padding: '20px' }}>
                    {/* Course Info */}
                    <Card style={{ marginBottom: 24 }}>
                        <Descriptions title={t('courseInformation')} bordered>
                            <Descriptions.Item label={t('code')}>{selectedCourse.code}</Descriptions.Item>
                            <Descriptions.Item label={t('courseName')}>{selectedCourse.name}</Descriptions.Item>
                            <Descriptions.Item label={t('fee')}>
                                {selectedCourse.discount > 0 ? (
                                    <span>
                                        <span style={{ textDecoration: 'line-through', color: '#999', marginRight: '8px' }}>
                                            {formatCurrency(selectedCourse.fee?.amount, selectedCourse.fee?.currency)}
                                        </span>
                                        <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                                            {formatCurrency((selectedCourse.fee?.amount || 0) - selectedCourse.discount, selectedCourse.fee?.currency)}
                                        </span>
                                    </span>
                                ) : (
                                    formatCurrency(selectedCourse.fee?.amount, selectedCourse.fee?.currency)
                                )}
                            </Descriptions.Item>
                            {selectedCourse.discount > 0 && (
                                <Descriptions.Item label={t('discount')}>
                                    <Tag color="green">-{formatCurrency(selectedCourse.discount, selectedCourse.fee?.currency)}</Tag>
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label={t('duration')}>
                                {selectedCourse.duration?.value} {selectedCourse.duration?.unit}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('status')}>
                                <Tag color={selectedCourse.isActive ? 'green' : 'red'}>
                                    {selectedCourse.isActive ? t('active') : t('completed')}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('instructor')}>
                                {selectedCourse.instructor
                                    ? `${selectedCourse.instructor.firstName} ${selectedCourse.instructor.lastName}`
                                    : '-'
                                }
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    {/* Statistics Cards - USD */}
                    {courseCurrency === 'USD' && (
                        <>
                            <Title level={4} style={{ marginBottom: 16, marginTop: 16 }}>USD {t('statistics')}</Title>
                            <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
                                <Col xs={24} sm={12} md={6}>
                                    <Card
                                        hoverable
                                        style={{
                                            borderRadius: '12px',
                                            boxShadow: '0 2px 8px rgba(82, 196, 26, 0.15)',
                                            border: '1px solid #f0f0f0',
                                            transition: 'all 0.3s'
                                        }}
                                    >
                                        <Statistic
                                            title={<span style={{ fontSize: '14px', fontWeight: '500' }}>{t('totalIncome')}</span>}
                                            value={formatCurrency(stats.totalIncomeUSD, 'USD')}
                                            prefix={<RiseOutlined style={{ fontSize: '18px', marginRight: '6px' }} />}
                                            suffix={<span style={{ fontSize: '13px', color: '#8c8c8c', marginLeft: '4px' }}>USD</span>}
                                            valueStyle={{ color: '#52c41a', fontSize: '20px', fontWeight: '600' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card
                                        hoverable
                                        style={{
                                            borderRadius: '12px',
                                            boxShadow: '0 2px 8px rgba(250, 173, 20, 0.15)',
                                            border: '1px solid #f0f0f0',
                                            transition: 'all 0.3s'
                                        }}
                                    >
                                        <Statistic
                                            title={<span style={{ fontSize: '14px', fontWeight: '500' }}>{t('totalCommission')}</span>}
                                            value={formatCurrency(stats.totalCommissionUSD, 'USD')}
                                            prefix={<TeamOutlined style={{ fontSize: '18px', marginRight: '6px' }} />}
                                            suffix={<span style={{ fontSize: '13px', color: '#8c8c8c', marginLeft: '4px' }}>USD</span>}
                                            valueStyle={{ color: '#faad14', fontSize: '20px', fontWeight: '600' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card
                                        hoverable
                                        style={{
                                            borderRadius: '12px',
                                            boxShadow: '0 2px 8px rgba(255, 77, 79, 0.15)',
                                            border: '1px solid #f0f0f0',
                                            transition: 'all 0.3s'
                                        }}
                                    >
                                        <Statistic
                                            title={<span style={{ fontSize: '14px', fontWeight: '500' }}>{t('totalExpenses')}</span>}
                                            value={formatCurrency(stats.totalExpensesUSD, 'USD')}
                                            prefix={<FallOutlined style={{ fontSize: '18px', marginRight: '6px' }} />}
                                            suffix={<span style={{ fontSize: '13px', color: '#8c8c8c', marginLeft: '4px' }}>USD</span>}
                                            valueStyle={{ color: '#ff4d4f', fontSize: '20px', fontWeight: '600' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card
                                        hoverable
                                        style={{
                                            borderRadius: '12px',
                                            boxShadow: stats.netProfitUSD >= 0 ? '0 2px 8px rgba(82, 196, 26, 0.15)' : '0 2px 8px rgba(255, 77, 79, 0.15)',
                                            border: '1px solid #f0f0f0',
                                            transition: 'all 0.3s'
                                        }}
                                    >
                                        <Statistic
                                            title={<span style={{ fontSize: '14px', fontWeight: '500' }}>{t('netProfit')}</span>}
                                            value={formatCurrency(stats.netProfitUSD, 'USD')}
                                            prefix={<DollarOutlined style={{ fontSize: '18px', marginRight: '6px' }} />}
                                            suffix={<span style={{ fontSize: '13px', color: '#8c8c8c', marginLeft: '4px' }}>USD</span>}
                                            valueStyle={{ color: stats.netProfitUSD >= 0 ? '#52c41a' : '#ff4d4f', fontSize: '20px', fontWeight: '600' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </>
                    )}

                    {/* Statistics Cards - IQD */}
                    {courseCurrency === 'IQD' && (
                        <>
                            <Title level={4} style={{ marginBottom: 16, marginTop: 16 }}>IQD {t('statistics')}</Title>
                            <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
                                <Col xs={24} sm={12} md={6}>
                                    <Card
                                        hoverable
                                        style={{
                                            borderRadius: '12px',
                                            boxShadow: '0 2px 8px rgba(82, 196, 26, 0.15)',
                                            border: '1px solid #f0f0f0',
                                            transition: 'all 0.3s'
                                        }}
                                    >
                                        <Statistic
                                            title={<span style={{ fontSize: '14px', fontWeight: '500' }}>{t('totalIncome')}</span>}
                                            value={formatCurrency(stats.totalIncomeIQD, 'IQD')}
                                            prefix={<RiseOutlined style={{ fontSize: '18px', marginRight: '6px' }} />}
                                            suffix={<span style={{ fontSize: '13px', color: '#8c8c8c', marginLeft: '4px' }}>IQD</span>}
                                            valueStyle={{ color: '#52c41a', fontSize: '20px', fontWeight: '600' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card
                                        hoverable
                                        style={{
                                            borderRadius: '12px',
                                            boxShadow: '0 2px 8px rgba(250, 173, 20, 0.15)',
                                            border: '1px solid #f0f0f0',
                                            transition: 'all 0.3s'
                                        }}
                                    >
                                        <Statistic
                                            title={<span style={{ fontSize: '14px', fontWeight: '500' }}>{t('totalCommission')}</span>}
                                            value={formatCurrency(stats.totalCommissionIQD, 'IQD')}
                                            prefix={<TeamOutlined style={{ fontSize: '18px', marginRight: '6px' }} />}
                                            suffix={<span style={{ fontSize: '13px', color: '#8c8c8c', marginLeft: '4px' }}>IQD</span>}
                                            valueStyle={{ color: '#faad14', fontSize: '20px', fontWeight: '600' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card
                                        hoverable
                                        style={{
                                            borderRadius: '12px',
                                            boxShadow: '0 2px 8px rgba(255, 77, 79, 0.15)',
                                            border: '1px solid #f0f0f0',
                                            transition: 'all 0.3s'
                                        }}
                                    >
                                        <Statistic
                                            title={<span style={{ fontSize: '14px', fontWeight: '500' }}>{t('totalExpenses')}</span>}
                                            value={formatCurrency(stats.totalExpensesIQD, 'IQD')}
                                            prefix={<FallOutlined style={{ fontSize: '18px', marginRight: '6px' }} />}
                                            suffix={<span style={{ fontSize: '13px', color: '#8c8c8c', marginLeft: '4px' }}>IQD</span>}
                                            valueStyle={{ color: '#ff4d4f', fontSize: '20px', fontWeight: '600' }}
                                        />
                                    </Card>
                                </Col>
                                <Col xs={24} sm={12} md={6}>
                                    <Card
                                        hoverable
                                        style={{
                                            borderRadius: '12px',
                                            boxShadow: stats.netProfitIQD >= 0 ? '0 2px 8px rgba(82, 196, 26, 0.15)' : '0 2px 8px rgba(255, 77, 79, 0.15)',
                                            border: '1px solid #f0f0f0',
                                            transition: 'all 0.3s'
                                        }}
                                    >
                                        <Statistic
                                            title={<span style={{ fontSize: '14px', fontWeight: '500' }}>{t('netProfit')}</span>}
                                            value={formatCurrency(stats.netProfitIQD, 'IQD')}
                                            prefix={<DollarOutlined style={{ fontSize: '18px', marginRight: '6px' }} />}
                                            suffix={<span style={{ fontSize: '13px', color: '#8c8c8c', marginLeft: '4px' }}>IQD</span>}
                                            valueStyle={{ color: stats.netProfitIQD >= 0 ? '#52c41a' : '#ff4d4f', fontSize: '20px', fontWeight: '600' }}
                                        />
                                    </Card>
                                </Col>
                            </Row>
                        </>
                    )}

                    {/* Student Count Card */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col span={24}>
                            <Card>
                                <Statistic
                                    title={t('totalStudents')}
                                    value={stats.studentCount}
                                    prefix={<UserOutlined />}
                                    valueStyle={{ color: '#1890ff' }}
                                />
                            </Card>
                        </Col>
                    </Row>

                    {/* Financial Overview Charts */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        {/* Income vs Expenses vs Commission Chart - USD */}
                        {courseCurrency === 'USD' && (
                            <Col xs={24} lg={12}>
                                <Card title={`${t('financialOverview')} (USD)`}>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={[
                                            { name: t('income'), value: stats.totalIncomeUSD, fill: '#52c41a' },
                                            { name: t('commission'), value: stats.totalCommissionUSD, fill: '#faad14' },
                                            { name: t('expenses'), value: stats.totalExpensesUSD, fill: '#ff4d4f' },
                                            { name: t('netProfit'), value: stats.netProfitUSD, fill: stats.netProfitUSD >= 0 ? '#1890ff' : '#ff7875' }
                                        ]}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                                            <Bar dataKey="value" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Card>
                            </Col>
                        )}

                        {/* Income vs Expenses vs Commission Chart - IQD */}
                        {courseCurrency === 'IQD' && (
                            <Col xs={24} lg={12}>
                                <Card title={`${t('financialOverview')} (IQD)`}>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={[
                                            { name: t('income'), value: stats.totalIncomeIQD, fill: '#52c41a' },
                                            { name: t('commission'), value: stats.totalCommissionIQD, fill: '#faad14' },
                                            { name: t('expenses'), value: stats.totalExpensesIQD, fill: '#ff4d4f' },
                                            { name: t('netProfit'), value: stats.netProfitIQD, fill: stats.netProfitIQD >= 0 ? '#1890ff' : '#ff7875' }
                                        ]}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip formatter={(value) => `IQD ${value.toLocaleString()}`} />
                                            <Bar dataKey="value" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Card>
                            </Col>
                        )}
                    </Row>

                    {/* Student Payment Status Pie Chart */}
                    {stats.students.length > 0 && (
                        <Row gutter={16} style={{ marginBottom: 24 }}>
                            <Col xs={24} lg={12}>
                                <Card title={t('studentPaymentStatus')}>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={[
                                                    { name: t('fullyPaid'), value: stats.students.filter(s => s.remainingAmount === 0).length },
                                                    { name: t('partiallyPaid'), value: stats.students.filter(s => s.remainingAmount > 0 && s.paidAmount > 0).length },
                                                    { name: t('unpaid'), value: stats.students.filter(s => s.paidAmount === 0).length }
                                                ]}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                <Cell fill="#52c41a" />
                                                <Cell fill="#faad14" />
                                                <Cell fill="#ff4d4f" />
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Card>
                            </Col>

                            {/* Revenue Distribution by Currency */}
                            <Col xs={24} lg={12}>
                                <Card title={t('revenueDistribution')}>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={[
                                                    { name: 'USD', value: stats.totalIncomeUSD },
                                                    { name: 'IQD', value: stats.totalIncomeIQD }
                                                ].filter(item => item.value > 0)}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ name, value }) => `${name}: ${name === 'USD' ? '$' : 'IQD '}${value.toLocaleString()}`}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                <Cell fill="#1890ff" />
                                                <Cell fill="#52c41a" />
                                            </Pie>
                                            <Tooltip formatter={(value, name) => [`${name === 'USD' ? '$' : 'IQD '}${value.toLocaleString()}`, name]} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Calculation Summary - USD */}
                    {courseCurrency === 'USD' && (
                        <Card title={`${t('profitCalculation')} (USD)`} style={{ marginBottom: 24 }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                    <Text strong>{t('totalIncome')}:</Text>
                                    <Text style={{ color: '#52c41a' }}>{formatCurrency(stats.totalIncomeUSD, 'USD')}</Text>
                                </div>
                                <Divider style={{ margin: '8px 0' }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                    <Text>{t('totalCommission')}:</Text>
                                    <Text style={{ color: '#faad14' }}>- {formatCurrency(stats.totalCommissionUSD, 'USD')}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                    <Text>{t('totalExpenses')}:</Text>
                                    <Text style={{ color: '#ff4d4f' }}>- {formatCurrency(stats.totalExpensesUSD, 'USD')}</Text>
                                </div>
                                <Divider style={{ margin: '8px 0' }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                    <Text strong style={{ fontSize: '16px' }}>{t('netProfit')}:</Text>
                                    <Text strong style={{ fontSize: '16px', color: stats.netProfitUSD >= 0 ? '#52c41a' : '#ff4d4f' }}>
                                        {formatCurrency(stats.netProfitUSD, 'USD')}
                                    </Text>
                                </div>
                            </Space>
                        </Card>
                    )}

                    {/* Calculation Summary - IQD */}
                    {courseCurrency === 'IQD' && (
                        <Card title={`${t('profitCalculation')} (IQD)`} style={{ marginBottom: 24 }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                    <Text strong>{t('totalIncome')}:</Text>
                                    <Text style={{ color: '#52c41a' }}>{formatCurrency(stats.totalIncomeIQD, 'IQD')}</Text>
                                </div>
                                <Divider style={{ margin: '8px 0' }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                    <Text>{t('totalCommission')}:</Text>
                                    <Text style={{ color: '#faad14' }}>- {formatCurrency(stats.totalCommissionIQD, 'IQD')}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                    <Text>{t('totalExpenses')}:</Text>
                                    <Text style={{ color: '#ff4d4f' }}>- {formatCurrency(stats.totalExpensesIQD, 'IQD')}</Text>
                                </div>
                                <Divider style={{ margin: '8px 0' }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                    <Text strong style={{ fontSize: '16px' }}>{t('netProfit')}:</Text>
                                    <Text strong style={{ fontSize: '16px', color: stats.netProfitIQD >= 0 ? '#52c41a' : '#ff4d4f' }}>
                                        {formatCurrency(stats.netProfitIQD, 'IQD')}
                                    </Text>
                                </div>
                            </Space>
                        </Card>
                    )}

                    {/* Enrolled Students Table */}
                    <Card title={t('enrolledStudents')} style={{ marginBottom: 24 }}>
                        <Table
                            columns={studentColumns}
                            dataSource={stats.students}
                            rowKey="studentId"
                            pagination={{ pageSize: 10 }}
                            loading={isLoading}
                            summary={(pageData) => {
                                let totalFeesUSD = 0;
                                let totalPaidUSD = 0;
                                let totalRemainingUSD = 0;
                                let totalFeesIQD = 0;
                                let totalPaidIQD = 0;
                                let totalRemainingIQD = 0;

                                pageData.forEach(({ totalFee, paidAmount, remainingAmount, currency }) => {
                                    if (currency === 'USD') {
                                        totalFeesUSD += totalFee || 0;
                                        totalPaidUSD += paidAmount || 0;
                                        totalRemainingUSD += remainingAmount || 0;
                                    } else {
                                        totalFeesIQD += totalFee || 0;
                                        totalPaidIQD += paidAmount || 0;
                                        totalRemainingIQD += remainingAmount || 0;
                                    }
                                });

                                return (
                                    <Table.Summary>
                                        {totalFeesUSD > 0 && (
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell index={0} colSpan={2}>
                                                    <Text strong>{t('total')} (USD)</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={2}>
                                                    <Text strong>{formatCurrency(totalFeesUSD, 'USD')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={3}>
                                                    <Text strong>{formatCurrency(totalPaidUSD, 'USD')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={4}>
                                                    <Text strong>{formatCurrency(totalRemainingUSD, 'USD')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={5} />
                                            </Table.Summary.Row>
                                        )}
                                        {totalFeesIQD > 0 && (
                                            <Table.Summary.Row>
                                                <Table.Summary.Cell index={0} colSpan={2}>
                                                    <Text strong>{t('total')} (IQD)</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={2}>
                                                    <Text strong>{formatCurrency(totalFeesIQD, 'IQD')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={3}>
                                                    <Text strong>{formatCurrency(totalPaidIQD, 'IQD')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={4}>
                                                    <Text strong>{formatCurrency(totalRemainingIQD, 'IQD')}</Text>
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={5} />
                                            </Table.Summary.Row>
                                        )}
                                    </Table.Summary>
                                );
                            }}
                        />
                    </Card >

                    {/* Staff Commissions Table - USD */}
                    {
                        courseCurrency === 'USD' && stats.staffCommissionsUSD.length > 0 && (
                            <Card title={`${t('staffCommissions')} (USD)`} style={{ marginBottom: 24 }}>
                                <Table
                                    columns={commissionColumns('USD')}
                                    dataSource={stats.staffCommissionsUSD}
                                    rowKey="staffId"
                                    pagination={false}
                                    loading={isLoading}
                                    summary={(pageData) => {
                                        let total = 0;
                                        pageData.forEach(({ commissionAmount }) => {
                                            total += commissionAmount || 0;
                                        });

                                        return (
                                            <Table.Summary>
                                                <Table.Summary.Row>
                                                    <Table.Summary.Cell index={0} colSpan={4}>
                                                        <Text strong>{t('totalCommission')}</Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={4}>
                                                        <Text strong>{formatCurrency(total, 'USD')}</Text>
                                                    </Table.Summary.Cell>
                                                </Table.Summary.Row>
                                            </Table.Summary>
                                        );
                                    }}
                                />
                            </Card>
                        )
                    }

                    {/* Staff Commissions Table - IQD */}
                    {
                        courseCurrency === 'IQD' && stats.staffCommissionsIQD.length > 0 && (
                            <Card title={`${t('staffCommissions')} (IQD)`} style={{ marginBottom: 24 }}>
                                <Table
                                    columns={commissionColumns('IQD')}
                                    dataSource={stats.staffCommissionsIQD}
                                    rowKey="staffId"
                                    pagination={false}
                                    loading={isLoading}
                                    summary={(pageData) => {
                                        let total = 0;
                                        pageData.forEach(({ commissionAmount }) => {
                                            total += commissionAmount || 0;
                                        });

                                        return (
                                            <Table.Summary>
                                                <Table.Summary.Row>
                                                    <Table.Summary.Cell index={0} colSpan={4}>
                                                        <Text strong>{t('totalCommission')}</Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={4}>
                                                        <Text strong>{formatCurrency(total, 'IQD')}</Text>
                                                    </Table.Summary.Cell>
                                                </Table.Summary.Row>
                                            </Table.Summary>
                                        );
                                    }}
                                />
                            </Card>
                        )
                    }

                    {/* Course Expenses Table - USD */}
                    {
                        courseCurrency === 'USD' && (
                            <Card title={`${t('courseExpenses')} (USD) - Total: ${stats.expensesUSD.length}`} style={{ marginBottom: 24 }}>
                                {stats.expensesUSD.length === 0 ? (
                                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                        {t('noExpensesFound') || 'No expenses found for this course'}
                                    </div>
                                ) : (
                                <Table
                                    columns={expenseColumns}
                                    dataSource={stats.expensesUSD}
                                    rowKey="id"
                                    pagination={false}
                                    loading={isLoading}
                                    summary={(pageData) => {
                                        let total = 0;
                                        pageData.forEach(({ amount }) => {
                                            total += amount || 0;
                                        });

                                        return (
                                            <Table.Summary>
                                                <Table.Summary.Row>
                                                    <Table.Summary.Cell index={0} colSpan={2}>
                                                        <Text strong>{t('totalExpenses')}</Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={2}>
                                                        <Text strong>{formatCurrency(total, 'USD')}</Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={3} />
                                                </Table.Summary.Row>
                                            </Table.Summary>
                                        );
                                    }}
                                />
                                )}
                            </Card>
                        )
                    }

                    {/* Course Expenses Table - IQD */}
                    {
                        courseCurrency === 'IQD' && (
                            <Card title={`${t('courseExpenses')} (IQD) - Total: ${stats.expensesIQD.length}`}>
                                {stats.expensesIQD.length === 0 ? (
                                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                        {t('noExpensesFound') || 'No expenses found for this course'}
                                    </div>
                                ) : (
                                <Table
                                    columns={expenseColumns}
                                    dataSource={stats.expensesIQD}
                                    rowKey="id"
                                    pagination={false}
                                    loading={isLoading}
                                    summary={(pageData) => {
                                        let total = 0;
                                        pageData.forEach(({ amount }) => {
                                            total += amount || 0;
                                        });

                                        return (
                                            <Table.Summary>
                                                <Table.Summary.Row>
                                                    <Table.Summary.Cell index={0} colSpan={2}>
                                                        <Text strong>{t('totalExpenses')}</Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={2}>
                                                        <Text strong>{formatCurrency(total, 'IQD')}</Text>
                                                    </Table.Summary.Cell>
                                                    <Table.Summary.Cell index={3} />
                                                </Table.Summary.Row>
                                            </Table.Summary>
                                        );
                                    }}
                                />
                                )}
                            </Card>
                        )
                    }
                </div >
            )}

            {
                !selectedCourse && (
                    <Card>
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <BookOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
                            <Title level={4} style={{ marginTop: 16, color: '#999' }}>
                                {t('selectCourseToViewReport')}
                            </Title>
                        </div>
                    </Card>
                )
            }
        </div >
    );
};

export default CourseReport;
